import { useState } from 'react';
import { useTranslations } from 'use-intl';
import { useAddProblem, useMatchObjectives } from '@/app/queries';
import { AiAnswerFrame, AiRefusal } from '@/ui/AiAnswer';
import { AiButton } from '@/ui/AiButton';
import { Button } from '@/ui/Button';
import { Chip } from '@/ui/Chip';
import { Notice } from '@/ui/Panel';

/**
 * Reading the coach's own match notes back to him, sharpened and ranked.
 *
 * Adding one goes through the same endpoint the hand-written form uses, and
 * promoting it to an objective goes through the same button underneath. There
 * is deliberately no shortcut from a suggestion straight to an objective: a
 * second way to create one is a second chain to keep honest, and the chain is
 * the product.
 */
export function AiMatchObjectives({ eventId }: { eventId: string }) {
  const t = useTranslations();
  const suggest = useMatchObjectives();
  const add = useAddProblem(eventId);
  const [added, setAdded] = useState<string[]>([]);
  const answer = suggest.data;
  const refusal = suggest.error as { code?: string } | null;

  return (
    <div className="grid gap-3 px-4 pb-4">
      <AiButton
        label={t('ai.rankProblems')}
        busy={suggest.isPending}
        onRun={() => suggest.mutate({ eventId })}
      />
      {suggest.isPending ? <Notice>{t('ai.thinkingMatch')}</Notice> : null}
      {refusal?.code ? <AiRefusal code={refusal.code} /> : null}

      {answer ? (
        <div className="overflow-hidden rounded-(--radius-panel) border border-line bg-surface">
          <AiAnswerFrame answer={answer}>
            <ul className="grid gap-3">
              {answer.result.problems.map((p) => (
                <li key={p.title} className="border-s-2 border-line ps-3">
                  <div className="flex flex-wrap items-baseline gap-2">
                    <span className="tabular text-meta text-ink-faint">{p.priority}</span>
                    <span className="font-medium">{p.title}</span>
                    <Chip muted>{t(`gameModel.phase_${p.phase}`)}</Chip>
                    <Chip muted>{p.dimension}</Chip>
                  </div>
                  <p className="mt-1 text-meta text-ink-muted">{p.detail}</p>
                  <p className="mt-1 text-meta">
                    <span className="text-ink-faint">{t('ai.wouldBecome')}</span>{' '}
                    {p.proposedObjective}
                  </p>
                  <div className="mt-2">
                    {added.includes(p.title) ? (
                      <span className="text-meta text-ok">{t('ai.problemAdded')}</span>
                    ) : (
                      <Button
                        type="button"
                        variant="secondary"
                        className="!text-meta"
                        busy={add.isPending}
                        onClick={async () => {
                          await add.mutateAsync({
                            title: p.title,
                            detail: p.detail,
                            phase: p.phase as never,
                            dimension: p.dimension as never,
                            priority: p.priority,
                          });
                          setAdded((a) => [...a, p.title]);
                        }}
                      >
                        {t('ai.addProblem')}
                      </Button>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          </AiAnswerFrame>
        </div>
      ) : null}
    </div>
  );
}
